import { ExpandMore, WarningRounded } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Grid, FormControlLabel, Radio, Typography, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import { FormAutocomplete, FormRadioGroup, FormTextField } from "../../../../../../../components/fields";
import { InvoiceType, ReactQueryKey, RouteParams } from "../../../../../../../types";
import { IFormValues } from "../../../../../types";
import getLegalEntities from "../../../../../../../api/services/getLegalEntities";
import { getCompanyData, useBookingByIdWithMetaQueryObserver } from "../../../../../../../api/services";
import ReissuedInvoiceWarning from "../../../../../../../components/InvoiceOwnerChangedWarning";

const LegalFormField = () => {

    const { t } = useTranslation('booking');

    const legalFormQuery = useQuery({
        queryKey: [ReactQueryKey.LegalForm],
        queryFn: getLegalEntities,
        refetchOnMount: false
    });

    return (
        <FormAutocomplete<IFormValues>
            name="billingOwnerDetails.legalEntity"
            label={t('label.legalForm')}
            options={legalFormQuery.data ?? []}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            getOptionLabel={(option) => { return !!option.label ?  t(`legalForms.${option.label}`) : ''}}
            readOnly={true}
        />
    )
}

const BillingOwnerDetails = () => {

    const { t } = useTranslation('booking');

    const billingOwnerDetails = useWatch<IFormValues, 'billingOwnerDetails'>({
        name: 'billingOwnerDetails'
    });

    const formContext = useFormContext();

    const { id } = useParams<RouteParams>();

    const companyDataMutation = useMutation(
        getCompanyData,
        {
            onSuccess: (data) => {
                formContext.setValue("billingOwnerDetails.siren", data.billingOwner.siren);
                formContext.setValue("billingOwnerDetails.siret", data.billingOwner.siret);
            },
            onError: () => {}
        }
    );

    const companyId = formContext.watch("billingOwnerDetails.sapId");
    const creditType = formContext.watch("creditType");

    useEffect(() => {
        if (!companyId) return;
        companyDataMutation.mutate({ companyId, creditType, firstInvoiceRecipient: "COMPANY" });
    }, [companyId, creditType])

    const bookingByIdQuery = useBookingByIdWithMetaQueryObserver(id);

    const { watch } = useFormContext<IFormValues>();
    const siren = watch(`bookingOwnerDetails.siren`);
    const siret = watch(`bookingOwnerDetails.siret`);
    const vatNumberCountry = watch(`bookingOwnerDetails.vatNumberCountry`);
    const residenceCountry = watch(`bookingOwnerDetails.country`);
    const billingCountry_code = watch(`billingOwnerDetails.billingCountry`);
    const hasInvoiceOwnerChanged = watch("billingOwnerDetails.hasInvoiceOwnerChanged");

    const checkSirenVisibility = () => {
        let visibility = !!vatNumberCountry ? vatNumberCountry === "FR" : billingCountry_code === "FR";
        return visibility;
    }

    return (
        <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                    <Typography variant="h5">
                        {t("label.billingOwner")}
                    </Typography>
                    {hasInvoiceOwnerChanged && 
                    <ReissuedInvoiceWarning tooltipTitle={t("label.reissuedInvoice")} />}
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container rowSpacing={1} columnSpacing={3}>
                    <Grid item xs={12}>
                        <FormRadioGroup<IFormValues>
                            name="billingOwnerDetails.type"
                            label=""
                            readOnly={true}
                        >
                            <FormControlLabel
                                value={InvoiceType.PERSON}
                                label={t("label.person")}
                                control={<Radio />}
                            />
                            <FormControlLabel
                                value={InvoiceType.VAT_NUMBER_HOLDER}
                                label={t("label.vatNumberHolder")}
                                control={<Radio />}
                            />
                            <FormControlLabel
                                value={InvoiceType.COMPANY}
                                label={t("label.company")}
                                control={<Radio />}
                            />
                        </FormRadioGroup>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="billingOwnerDetails.id"
                            label={t("label.id")}
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>

                    {billingOwnerDetails.type !== InvoiceType.PERSON &&
                    <Grid item xs={12} sm={6}>
                        <LegalFormField />
                    </Grid>
                    }

                    {billingOwnerDetails.type !== InvoiceType.PERSON &&
                        <Grid item xs={12} sm={6}>
                            <FormTextField<IFormValues>
                                name="billingOwnerDetails.companyName"
                                label={t("label.companyName")}
                                InputProps={{
                                    readOnly: true
                                }}
                                InputLabelProps={{
                                    shrink: true
                                }}
                            />
                        </Grid>
                    }
                    {billingOwnerDetails.type === InvoiceType.PERSON &&
                        <Grid item xs={12} sm={6}>
                            <FormTextField<IFormValues>
                                name="billingOwnerDetails.firstName"
                                label={t("label.firstName")}
                                InputProps={{
                                    readOnly: true
                                }}
                                InputLabelProps={{
                                    shrink: true
                                }}
                            />
                        </Grid>
                    }
                    {billingOwnerDetails.type === InvoiceType.PERSON &&
                        <Grid item xs={12} sm={6}>
                            <FormTextField<IFormValues>
                                name="billingOwnerDetails.lastName"
                                label={t("label.lastName")}
                                InputProps={{
                                    readOnly: true
                                }}
                                InputLabelProps={{
                                    shrink: true
                                }}
                            />
                        </Grid>
                    }
                    {billingOwnerDetails.type === InvoiceType.PERSON &&
                        <Grid item xs={12} sm={6}>
                            <FormTextField<IFormValues>
                                name="billingOwnerDetails.birthDate"
                                label={t("label.birthDate")}
                                InputProps={{
                                    readOnly: true
                                }}
                                InputLabelProps={{
                                    shrink: true
                                }}
                            />
                        </Grid>
                    }
                    {billingOwnerDetails.type === InvoiceType.PERSON &&
                        <Grid item xs={12} sm={6}>
                            <FormTextField<IFormValues>
                                name="billingOwnerDetails.birthNation"
                                label={t("label.nationOfBirth")}
                                InputProps={{
                                    readOnly: true
                                }}
                                InputLabelProps={{
                                    shrink: true
                                }}
                            />
                        </Grid>
                    }
                    {billingOwnerDetails.type === InvoiceType.PERSON &&
                        <Grid item xs={12} sm={6}>
                            <FormTextField<IFormValues>
                                name="billingOwnerDetails.birthPlace"
                                label={t("label.birthPlace")}
                                InputProps={{
                                    readOnly: true
                                }}
                                InputLabelProps={{
                                    shrink: true
                                }}
                            />
                        </Grid>
                    }
                    {billingOwnerDetails.type === InvoiceType.PERSON &&
                        <Grid item xs={12} sm={6}>
                            <FormTextField<IFormValues>
                                name="billingOwnerDetails.residenceCountry"
                                label={t("label.countryOfResidence")}
                                InputProps={{
                                    readOnly: true
                                }}
                                InputLabelProps={{
                                    shrink: true
                                }}
                            />
                        </Grid>
                    }
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="billingOwnerDetails.fiscalCode"
                            label={t("label.fiscalCode")}
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="billingOwnerDetails.email"
                            label={t("label.email")}
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />

                    </Grid>
                    {billingOwnerDetails.type === InvoiceType.PERSON &&
                        <>
                            <Grid item xs={12} sm={2}>
                                <FormTextField<IFormValues>
                                    name="billingOwnerDetails.mobilePrefix"
                                    label={t('label.prefix')}
                                    InputProps={{
                                        readOnly: true
                                    }}
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormTextField<IFormValues>
                                    name="billingOwnerDetails.mobile"
                                    label={t("label.mobile")}
                                    InputProps={{
                                        readOnly: true
                                    }}
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                />
                            </Grid>
                        </>
                    }
                    {billingOwnerDetails.type === InvoiceType.PERSON &&
                        <Grid item xs={12} sm={6}>
                            <FormTextField<IFormValues>
                                name="billingOwnerDetails.drivingLicenseNumber"
                                label={t("label.drivingLicenseNumber")}
                                InputProps={{
                                    readOnly: true
                                }}
                                InputLabelProps={{
                                    shrink: true
                                }}
                            />
                        </Grid>
                    }
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="billingOwnerDetails.billingCountry"
                            label={t("label.billingCountry")}
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="billingOwnerDetails.billingProvince"
                            label={t("label.billingProvince")}
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="billingOwnerDetails.billingCity"
                            label={t("label.billingCity")}
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="billingOwnerDetails.billingStreetName"
                            label={t("label.billingStreetName")}
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="billingOwnerDetails.billingStreetNumber"
                            label={t("label.billingStreetNumber")}
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="billingOwnerDetails.billingPostalCode"
                            label={t("label.billingPostalCode")}
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>

                    {checkSirenVisibility() &&
                        <>
                            <Grid item xs={12} sm={6}>
                                <FormTextField<IFormValues>
                                    name="billingOwnerDetails.siren"
                                    label={t("label.siren")}
                                    InputProps={{
                                        readOnly: true
                                    }}
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormTextField<IFormValues>
                                    name="billingOwnerDetails.siret"
                                    label={t("label.siret")}
                                    InputProps={{
                                        readOnly: true
                                    }}
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                />
                            </Grid>
                        </>
                    }

                    {!checkSirenVisibility() &&
                        <Grid item xs={12} sm={6}>
                            <FormTextField<IFormValues>
                                name="billingOwnerDetails.certifiedEmailAddress"
                                label={t("label.certifiedEmailAddress")}
                                InputProps={{
                                    readOnly: true
                                }}
                                InputLabelProps={{
                                    shrink: true
                                }}
                            />
                        </Grid>
                    }
                    {billingOwnerDetails.type !== InvoiceType.PERSON &&
                        <>
                            {(bookingByIdQuery.data?.booking?.invoiceCompany?.publicAuthority === '0' && !checkSirenVisibility()) &&
                                <Grid item xs={12} sm={6}>
                                    <FormTextField<IFormValues>
                                        name="billingOwnerDetails.invoiceCode"
                                        label={t("label.invoiceCode")}
                                        InputProps={{
                                            readOnly: true
                                        }}
                                        InputLabelProps={{
                                            shrink: true
                                        }}
                                    />
                                </Grid>
                            }

                            {bookingByIdQuery.data?.booking?.invoiceCompany?.publicAuthority === '1' &&
                                <>
                                    <Grid item xs={12} sm={6}>
                                        <FormTextField<IFormValues>
                                            name="billingOwnerDetails.publicAuthority"
                                            label={t("label.publicAuthority")}
                                            InputProps={{
                                                readOnly: true
                                            }}
                                            InputLabelProps={{
                                                shrink: true
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                            <FormTextField<IFormValues>
                                            name="billingOwnerDetails.paIdNumber"
                                            label={t("label.paIdNumber")}
                                            InputProps={{
                                            readOnly: true
                                            }}
                                                InputLabelProps={{
                                                shrink: true
                                            }}
                                            />
                                    </Grid>
                                </>
                            }
                            <Grid item xs={12} sm={6}>
                                <FormTextField<IFormValues>
                                    name="billingOwnerDetails.vatNumberCountry"
                                    label={t("label.vatNumberCountry")}
                                    InputProps={{
                                        readOnly: true
                                    }}
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormTextField<IFormValues>
                                    name="billingOwnerDetails.vatNumber"
                                    label={t("label.vatNumber")}
                                    InputProps={{
                                        readOnly: true
                                    }}
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                />
                            </Grid>
                        </>
                    }
                </Grid>
            </AccordionDetails>
        </Accordion>
    )
};

export default BillingOwnerDetails;
